import { cn } from '@/utils'
import { Button, Spinner } from '@/components/ui'
import { Badge } from '@/components/ui'
import {
  Volume2,
  VolumeX,
  Mic,
  MicOff,
  Settings,
  ChevronDown,
  Radio,
  AudioLines,
  Languages,
} from 'lucide-react'
import { useState } from 'react'
import type { VoiceConversationState, VoiceConversationMode, VoiceEngineType } from '@/types/voice'
import { STTLanguagePicker } from '@/components/voice/STTLanguagePicker'

interface VoiceOption {
  id: string
  name: string
}

interface VoiceControlBarProps {
  // TTS state
  ttsEnabled: boolean
  onTtsToggle: (enabled: boolean) => void
  ttsSpeaking: boolean
  ttsBuffering?: boolean  // For streaming mode
  ttsReady: boolean
  onTtsStop?: () => void

  // STT state
  sttEnabled: boolean
  onSttToggle: (enabled: boolean) => void
  sttSupported: boolean
  sttListening: boolean
  sttError?: string | null

  // Voice selection
  voices: VoiceOption[]
  selectedVoiceId: string | null
  onVoiceSelect: (voiceId: string) => void
  /** The persona's configured voice ID (if any) - used to show override indicator */
  personaVoiceId?: string | null

  // Streaming mode indicator
  isStreamingMode?: boolean

  // Voice conversation state (replaces STT section when active)
  voiceConversationActive?: boolean
  voiceConversationState?: VoiceConversationState
  voiceConversationMode?: VoiceConversationMode
  isOpenMicActive?: boolean
  isSpeechDetected?: boolean
  onSwitchToBasicStt?: () => void
  voiceEngineType?: VoiceEngineType

  // STT language override (conversation-level)
  /** Conversation-level language override (or null for "use persona/user default") */
  sttLanguageOverride?: string | null
  /** Persona's configured STT language (or null) — used to detect override */
  personaSttLanguage?: string | null
  onSttLanguageChange?: (code: string | null) => void

  className?: string
}

export function VoiceControlBar({
  ttsEnabled,
  onTtsToggle,
  ttsSpeaking,
  ttsBuffering,
  ttsReady,
  onTtsStop,
  sttEnabled,
  onSttToggle,
  sttSupported,
  sttListening,
  sttError,
  voices,
  selectedVoiceId,
  onVoiceSelect,
  personaVoiceId,
  isStreamingMode,
  voiceConversationActive = false,
  voiceConversationState = 'idle',
  voiceConversationMode = 'push-to-talk',
  isOpenMicActive = false,
  isSpeechDetected = false,
  onSwitchToBasicStt,
  voiceEngineType,
  sttLanguageOverride = null,
  personaSttLanguage,
  onSttLanguageChange,
  className,
}: VoiceControlBarProps) {
  const [showVoiceSelector, setShowVoiceSelector] = useState(false)

  const selectedVoice = voices.find((v) => v.id === selectedVoiceId)
  const personaVoice = personaVoiceId ? voices.find((v) => v.id === personaVoiceId) : null

  // Check if the current selection is an override of the persona's voice
  const isVoiceOverride = personaVoiceId && selectedVoiceId && selectedVoiceId !== personaVoiceId

  // Determine if TTS is active (speaking or buffering)
  const isActive = ttsSpeaking || ttsBuffering

  // Status text for TTS. A voice that speaks reads so whatever the toggle
  // says: an edge-local Kokoro persona speaks with voice off (SA-1397).
  const getTtsStatusText = () => {
    if (ttsBuffering) return 'Buffering...'
    if (ttsSpeaking) return 'Speaking...'
    if (!ttsEnabled) return 'Voice Off'
    return isStreamingMode ? 'Streaming' : 'Voice On'
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-4 py-2 bg-muted border-b border-border',
        className
      )}
    >
      {/* TTS Toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={ttsEnabled ? 'default' : 'ghost'}
          size="sm"
          onClick={() => {
            // Whatever is speaking can always be stopped, even with voice
            // toggled off (an edge-local Kokoro persona speaks regardless).
            if (isActive) {
              onTtsStop?.()
            } else {
              onTtsToggle(!ttsEnabled)
            }
          }}
          disabled={!ttsReady}
          className="gap-1.5"
          title={
            isActive
              ? 'Stop speaking'
              : ttsEnabled
                ? 'Disable voice responses'
                : 'Enable voice responses'
          }
        >
          {ttsEnabled || isActive ? (
            <Volume2
              className={cn(
                'w-4 h-4',
                ttsBuffering && 'animate-pulse opacity-70',
                // The primary-foreground tone suits only the filled (voice on)
                // button; on the ghost button it all but vanishes (SA-1397).
                ttsEnabled && ttsSpeaking && !ttsBuffering && 'text-primary-foreground/80'
              )}
            />
          ) : (
            <VolumeX className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">{getTtsStatusText()}</span>
        </Button>

        {/* Low-latency mode indicator */}
        {ttsEnabled && isStreamingMode && (
          <Badge variant="success" className="text-xs">
            Low-latency
          </Badge>
        )}

        {!ttsReady && (
          <Badge variant="warning" className="text-xs">
            Add API key in Settings
          </Badge>
        )}
      </div>

      {/* Divider */}
      <div className="h-4 w-px bg-border" />

      {/* STT / Voice Conversation Section */}
      {voiceConversationActive ? (
        <div className="flex items-center gap-2">
          {voiceEngineType && (
            <Badge
              variant={voiceEngineType === 'openai-realtime' ? 'success' : 'secondary'}
              className="text-[10px] px-1.5 py-0"
            >
              {voiceEngineType === 'openai-realtime' ? 'Realtime' : 'Pipeline'}
            </Badge>
          )}
          <VoiceConversationStatus
            state={voiceConversationState}
            mode={voiceConversationMode}
            isOpenMicActive={isOpenMicActive}
            isSpeechDetected={isSpeechDetected}
            onSwitchToBasicStt={onSwitchToBasicStt}
          />

          {/* STT Language Override */}
          {onSttLanguageChange && (
            <div className="flex items-center gap-1">
              <Languages className="w-4 h-4 text-muted-foreground shrink-0" />
              <STTLanguagePicker
                value={sttLanguageOverride}
                onChange={onSttLanguageChange}
                nullSentinel="use-persona-default"
                className="w-40 h-8 text-xs"
              />
              {sttLanguageOverride !== null && sttLanguageOverride !== personaSttLanguage && (
                <Badge variant="warning" className="text-xs">
                  Override
                </Badge>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            variant={sttEnabled ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onSttToggle(!sttEnabled)}
            disabled={!sttSupported}
            className="gap-1.5"
            title={
              !sttSupported
                ? 'Speech recognition not supported in this browser'
                : sttEnabled
                  ? 'Disable voice input'
                  : 'Enable voice input (click mic in chat to speak)'
            }
          >
            {sttEnabled ? (
              <Mic className={cn('w-4 h-4', sttListening && 'text-destructive animate-pulse')} />
            ) : (
              <MicOff className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {sttEnabled ? (sttListening ? 'Listening...' : 'Mic On') : 'Mic Off'}
            </span>
          </Button>

          {!sttSupported && (
            <Badge variant="secondary" className="text-xs">
              Use Chrome/Edge
            </Badge>
          )}

          {sttError && (
            <Badge variant="warning" className="text-xs max-w-32 truncate" title={sttError}>
              Error
            </Badge>
          )}
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Voice selector (only shown when TTS is available) */}
      {ttsReady && voices.length > 0 && (
        <div className="relative flex items-center gap-1.5">
          {/* Override indicator */}
          {isVoiceOverride && (
            <Badge variant="warning" className="text-xs">
              Override
            </Badge>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowVoiceSelector(!showVoiceSelector)}
            className="gap-1.5"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline max-w-24 truncate">
              {selectedVoice?.name || 'Select voice'}
            </span>
            <ChevronDown className="w-3 h-3" />
          </Button>

          {/* Dropdown */}
          {showVoiceSelector && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowVoiceSelector(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-64 max-h-64 overflow-y-auto z-20 bg-popover border border-border rounded-lg shadow-lg">
                <div className="p-2">
                  {/* Header with explanation */}
                  <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                    {personaVoice
                      ? 'Select voice (overrides persona default)'
                      : 'Select Voice'}
                  </p>

                  {/* Show persona default option if one is configured */}
                  {personaVoice && (
                    <>
                      <button
                        onClick={() => {
                          onVoiceSelect(personaVoice.id)
                          setShowVoiceSelector(false)
                        }}
                        className={cn(
                          'w-full text-left px-2 py-1.5 rounded-sm text-sm transition-colors flex items-center justify-between',
                          personaVoice.id === selectedVoiceId
                            ? 'bg-accent text-primary'
                            : 'hover:bg-accent'
                        )}
                      >
                        <span>{personaVoice.name}</span>
                        <Badge variant="secondary" className="text-xs ml-2">
                          Persona default
                        </Badge>
                      </button>
                      <div className="my-1 border-t border-border" />
                    </>
                  )}

                  {/* All voices */}
                  {voices
                    .filter((v) => v.id !== personaVoiceId) // Don't show persona voice twice
                    .map((voice) => (
                      <button
                        key={voice.id}
                        onClick={() => {
                          onVoiceSelect(voice.id)
                          setShowVoiceSelector(false)
                        }}
                        className={cn(
                          'w-full text-left px-2 py-1.5 rounded-sm text-sm transition-colors',
                          voice.id === selectedVoiceId
                            ? 'bg-accent text-primary'
                            : 'hover:bg-accent'
                        )}
                      >
                        {voice.name}
                      </button>
                    ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Voice Conversation Status (replaces STT section when voice conversation active)
// ---------------------------------------------------------------------------

interface VoiceConversationStatusProps {
  state: VoiceConversationState
  mode: VoiceConversationMode
  isOpenMicActive: boolean
  isSpeechDetected: boolean
  onSwitchToBasicStt?: () => void
}

function VoiceConversationStatus({
  state,
  mode,
  isOpenMicActive,
  isSpeechDetected,
  onSwitchToBasicStt,
}: VoiceConversationStatusProps) {
  const getIcon = () => {
    switch (state) {
      case 'connecting':
        return <Spinner size="sm" />
      case 'listening':
        if (isOpenMicActive && isSpeechDetected) {
          return <Mic className="w-4 h-4 animate-pulse" />
        }
        if (isOpenMicActive) {
          return <Radio className="w-4 h-4" />
        }
        // PTT listening
        return <Mic className="w-4 h-4 animate-pulse" />
      case 'processing':
        return <Spinner size="sm" />
      case 'speaking':
        return <AudioLines className="w-4 h-4" />
      case 'error':
        return <Mic className="w-4 h-4" />
      default: // idle
        return mode === 'open-mic' ? <Radio className="w-4 h-4" /> : <Mic className="w-4 h-4" />
    }
  }

  const getStatusText = () => {
    switch (state) {
      case 'connecting':
        return 'Connecting...'
      case 'listening':
        if (isOpenMicActive && isSpeechDetected) return 'Recording...'
        if (isOpenMicActive) return 'Open Mic Active'
        return 'Listening...'
      case 'processing':
        return 'Processing...'
      case 'speaking':
        return 'AI Speaking...'
      case 'error':
        return 'Error'
      default: // idle
        return mode === 'open-mic' ? 'Open Mic' : 'Push-to-Talk'
    }
  }

  const getButtonClasses = () => {
    // Open-mic active + listening + no speech = success (green)
    if (state === 'listening' && isOpenMicActive && !isSpeechDetected) {
      return 'bg-success hover:bg-success/90 text-success-foreground gap-1.5'
    }
    // Listening (PTT or speech detected) = danger (red)
    if (state === 'listening') {
      return 'gap-1.5'
    }
    // Speaking / processing = primary
    if (state === 'speaking' || state === 'processing') {
      return 'gap-1.5'
    }
    return 'gap-1.5'
  }

  const getButtonVariant = (): 'default' | 'ghost' | 'destructive' | 'secondary' => {
    if (state === 'listening' && isOpenMicActive && !isSpeechDetected) {
      // Custom green — handled via className override
      return 'default'
    }
    if (state === 'listening') return 'destructive'
    if (state === 'speaking' || state === 'processing' || state === 'connecting') return 'default'
    if (state === 'error') return 'ghost'
    return 'ghost'
  }

  const showActiveBadge = isOpenMicActive && ['listening', 'processing', 'speaking'].includes(state)

  return (
    <div className="flex items-center gap-2">
      <Button
        variant={getButtonVariant()}
        size="sm"
        onClick={onSwitchToBasicStt}
        className={getButtonClasses()}
        title="Click to switch to browser voice input"
      >
        {getIcon()}
        <span className="hidden sm:inline">{getStatusText()}</span>
      </Button>

      {showActiveBadge && (
        <Badge variant="success" className="text-xs">
          Active
        </Badge>
      )}

      {state === 'error' && (
        <Badge variant="warning" className="text-xs">
          Error
        </Badge>
      )}
    </div>
  )
}
